import { Eyebrow } from '../concept/eyebrow';
import { ActionButton } from '../concept/action-button';
import { Spinner } from '../primitives/spinner';

/**
 * The states before a coach surface has data.
 *
 * These were one string — "Loading client…" — rendered whenever clientData was
 * absent, which is also what an unauthenticated visitor sees. Nothing ever
 * set it, because nothing had been fetched, because nobody was signed in. The
 * page waited forever for something that was never going to arrive.
 *
 * They are different states and read differently:
 *
 *   checking   Auth0 is resolving a session. Brief, and genuinely loading.
 *   signed-out Nothing will load until someone signs in. Say so, and offer it.
 *   loading    Signed in, request in flight.
 *   error      It failed. Say what, and offer to try again.
 */
export type SurfaceState = 'checking' | 'signed-out' | 'loading' | 'error';

const Frame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex min-h-[100dvh] items-center justify-center bg-shell p-4 font-sans">
    <div className="w-full max-w-sm rounded-lg border border-border bg-card p-8 text-center">
      {children}
    </div>
  </div>
);

export const SignInPanel: React.FC<{
  state: SurfaceState;
  /** What is being waited for, e.g. "client" or "clients". */
  subject?: string;
  error?: string;
  /** Small print under the error, e.g. "Error 500" — for support, not the reader. */
  errorDetail?: string;
  onSignIn?: () => void;
  onRetry?: () => void;
  /** A way out when retrying cannot help — a wrong id, a foreign account. */
  onBack?: () => void;
  backLabel?: string;
}> = ({
  state,
  subject = 'data',
  error,
  errorDetail,
  onSignIn,
  onRetry,
  onBack,
  backLabel = 'Back to clients',
}) => {
  if (state === 'checking' || state === 'loading') {
    return (
      <Frame>
        <div className="flex flex-col items-center gap-3">
          <Spinner size={24} />
          <p className="text-sm text-muted-foreground">
            {state === 'checking' ? 'Checking your session…' : `Loading ${subject}…`}
          </p>
        </div>
      </Frame>
    );
  }

  if (state === 'error') {
    return (
      <Frame>
        <Eyebrow>Sizewall</Eyebrow>
        <h1 className="mt-2 text-xl font-semibold tracking-tight">
          Could not load {subject}
        </h1>
        <p data-testid="surface-error" className="mt-1 text-sm text-foreground-soft">
          {error || 'The request did not come back.'}
        </p>
        {errorDetail && (
          <p data-testid="surface-error-detail" className="tabular mt-2 text-[10.5px] text-muted-foreground">
            {errorDetail}
          </p>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {onRetry && (
            <ActionButton variant="primary" onClick={onRetry} testId="surface-retry">
              Try again
            </ActionButton>
          )}
          {onBack && (
            <ActionButton onClick={onBack} testId="surface-back">
              {backLabel}
            </ActionButton>
          )}
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <Eyebrow>Sizewall</Eyebrow>
      <h1 className="mt-2 text-xl font-semibold tracking-tight">Coach sign-in</h1>
      <p className="mt-1 text-sm text-foreground-soft">
        Sign in to reach your clients and their scans.
      </p>
      <ActionButton variant="primary" className="mt-6 w-full" onClick={onSignIn}>
        Log in
      </ActionButton>
    </Frame>
  );
};
